import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {
    markMinorConversions,
    type MinorConversionId,
} from "@khan/analytics-learning-time";
import {GoogleButton} from "@khan/auth-google";

import {LastChanceCollageHeight} from "./collages/last-chance.tsx";
import Illustration from "./illustration.tsx";
import * as mq from "./media-queries.ts";
import {SerifHeadingMedium} from "./typography/typography.ts";

type ButtonCTAProps = {
    type: "button";
    copy: string;
    href: string;
    conversion?: MinorConversionId;
};

type GoogleCTAProps = {
    type: "google";
};

type LastChanceCTAProps = ButtonCTAProps | GoogleCTAProps;

type Props = {
    kind: "LastChance";
    headline: string;
    ctas: Array<LastChanceCTAProps>;
};

export default class LastChance extends React.Component<Props> {
    render(): React.ReactNode {
        const {headline, ctas} = this.props;

        return (
            <View>
                <View style={styles.container}>
                    <SerifHeadingMedium tag="h2" style={styles.heading}>
                        {headline}
                    </SerifHeadingMedium>

                    <View style={styles.content}>
                        <View style={styles.illustration}>
                            <Illustration name="LastChanceCollage" />
                        </View>
                        <View style={styles.buttonContainer}>
                            {ctas.map((cta, i) => {
                                if (cta.type === "button") {
                                    return (
                                        <Button
                                            key={i}
                                            size="large"
                                            href={cta.href}
                                            safeWithNav={async () => {
                                                if (cta.conversion) {
                                                    await markMinorConversions([
                                                        {
                                                            id: cta.conversion,
                                                        },
                                                    ]);
                                                }
                                            }}
                                            style={styles.button}
                                        >
                                            {cta.copy}
                                        </Button>
                                    );
                                }
                                if (cta.type === "google") {
                                    return (
                                        <View style={styles.button} key={i}>
                                            <GoogleButton
                                                size="large"
                                                role="teacher"
                                                continueUrl="/"
                                                minorConversions={[
                                                    {
                                                        id: "last_chance_google_signup_clicked",
                                                    },
                                                ]}
                                            />
                                        </View>
                                    );
                                }
                            })}
                        </View>
                    </View>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
        margin: "120px 0 166px 0",
        padding: 0,
        [mq.smallDesktopOrSmaller]: {
            justifyContent: "center",
        },
        ":focus": {
            outline: 0,
        },
    },
    heading: {
        maxWidth: 700,
        textAlign: "center",

        [mq.tabletOnly]: {
            maxWidth: 550,
        },

        [mq.smallDesktopOnly]: {
            maxWidth: 550,
        },

        [mq.smallTabletOrSmaller]: {
            maxWidth: 440,
            paddingLeft: 20,
            paddingRight: 20,
        },
    },
    content: {
        position: "relative",
        width: "100%",
        margin: "24px 0 0 0",
        padding: "24px 0 24px 0",
        minHeight: LastChanceCollageHeight,
        alignItems: "center",

        [mq.smallDesktopOrSmaller]: {
            width: "100%",
            marginTop: 32,
            paddingTop: 32,
        },
        [mq.largePhoneOrSmaller]: {
            marginTop: 16,
            paddingTop: 16,
            paddingLeft: 50,
            paddingRight: 50,
        },
    },
    illustration: {
        position: "absolute",
        zIndex: -1,
        top: -34,

        [mq.tabletOrSmaller]: {
            top: -44,
        },

        [mq.largePhoneOrSmaller]: {
            transform: "scale(0.78)",
            top: -90,
        },
    },
    buttonContainer: {
        alignItems: "center",
        [mq.smallDesktopOrSmaller]: {
            width: "100%",
        },
    },
    button: {
        minWidth: 350,
        margin: "0 0 8px 0",

        [mq.largePhoneOrSmaller]: {
            minWidth: 0,
            width: "100%",
            maxWidth: 335,
        },
    },
});
